import { message, Form } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import RadioCards from 'components/RadioCards/RadioCards';
import { useState, useEffect } from 'react';
import { providerService } from 'services';

const SchoolCredentials = ({ careFacility, isEditable = false }) => {
  const [localCareFacility, setLocalCareFacility] = useState(careFacility);
  const [form] = Form.useForm();

  // Update local state when careFacility prop changes
  useEffect(() => {
    setLocalCareFacility(careFacility);
  }, [careFacility]);

  const { isEditing, editProps } = useEditCard({
    id: 'school-credentials',
    onEditStart: () => {
      const selectedValues = [];
      if (localCareFacility?.recognizedSchool) selectedValues.push('recognizedSchool');
      if (localCareFacility?.licensedSchool) selectedValues.push('licensedSchool');
      form.setFieldsValue({ schoolQualifications: selectedValues });
    },
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const updatePayload = {
        recognizedSchool: values.schoolQualifications?.includes('recognizedSchool') || false,
        licensedSchool: values.schoolQualifications?.includes('licensedSchool') || false,
      };
      const updatedCareFacility = await providerService.updateCenter(updatePayload, careFacility.id);
      setLocalCareFacility(updatedCareFacility);
      form.resetFields();
      message.success('School credentials updated');
    },
    onDiscard: () => form.resetFields(),
  });

  return (
    <Card title="School credentials" extra={isEditable ? <CardEdit {...editProps} /> : null} stickyHeader noBodyPadding>
      <TabularList
        indented
        data={[
          {
            title: 'School qualifications',
            titleRequired: true,
            value: isEditing ? (
              <Form form={form} disabled={editProps.loading} layout="vertical">
                <Form.Item
                  name="schoolQualifications"
                  className="mb-0"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || value.length === 0) {
                          return Promise.reject('At least one qualification must be selected');
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <RadioCards
                    multiple
                    size="small"
                    options={[
                      {
                        value: 'recognizedSchool',
                        label: 'Recognized by local school board',
                        description:
                          'Recognized by the local school board as a public or private school, preschool program, or secondary education institution',
                      },
                      {
                        value: 'licensedSchool',
                        label: 'Has a valid state issued license',
                      },
                    ]}
                  />
                </Form.Item>
              </Form>
            ) : (
              <ul className="list-disc ml-4">
                {localCareFacility?.recognizedSchool && (
                  <li>
                    Recognized by the local school board as a public or private school, preschool program, or secondary
                    education institution
                  </li>
                )}

                {localCareFacility?.licensedSchool && <li>Has a valid state issued license</li>}

                {!localCareFacility?.recognizedSchool && !localCareFacility?.licensedSchool && (
                  <div className="text-gray-500">None selected</div>
                )}
              </ul>
            ),
          },
        ]}
      />
    </Card>
  );
};

export { SchoolCredentials };
